import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import clsx from 'clsx';
import styles from './RelatedCompaniesNetwork.module.scss';
import {
    filterIconMUDark,
    filterIconMUPurpleDark,
    noDataFoundEmptyState,
    purpleOfficeIcon,
    purpleRedirectIcon,
} from '../../../../../../assets/hostedassets';
import Tooltip from '../../../../../../common/_custom/Tooltip/Tooltip';
import { AICA_REPORT_AVAILABLE_TOOLTIP, SORT_DIRECTIONS, SortConfig } from '../../../../../../utils/constants/commonConstants';
import SearchIcon from '../../../../../../assets/Svg/SearchIcon';
import SortIcon from '../../../../../Investor/Deals/components/DealsList/DealsTable/components/SortIcon/SortIcon';
import { CompaniesNetworkData, NetworkRelatedPartyCompany, NetworkRelatedPartyEntity, StatusOption } from './types';

// ─── Shared utilities (re-used from drawer) ───────────────────────────────────

function getStatusMeta(status: string | undefined, options: StatusOption[]): StatusOption | null {
    if (!status) return null;
    return options.find((opt) => opt?.value?.toLowerCase?.() === status?.toLowerCase?.()) ?? null;
}

function toRelationshipLabel(relationshipType: string): string {
    return relationshipType
        .split('_')
        .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
        .join(' ');
}

// ─── Filter Dropdown ──────────────────────────────────────────────────────────

type FilterDropdownProps = {
    anchorRef: React.RefObject<HTMLButtonElement>;
    options: StatusOption[];
    selectedValues: string[];
    search: string;
    onSearchChange: (val: string) => void;
    onToggleOption: (val: string) => void;
};

const FilterDropdown: React.FC<FilterDropdownProps> = ({ anchorRef, options, selectedValues, search, onSearchChange, onToggleOption }) => {
    const [pos, setPos] = useState({ top: 0, left: 0 });
    const dropdownRef = useRef<HTMLDivElement>(null);

    const DROPDOWN_OFFSET_Y = 4;
    const DROPDOWN_FALLBACK_WIDTH = 220;
    const SCREEN_EDGE_PADDING = 8;

    React.useLayoutEffect(() => {
        if (!anchorRef.current) return;
        const rect = anchorRef.current.getBoundingClientRect();
        const dropdownWidth = dropdownRef.current?.offsetWidth ?? DROPDOWN_FALLBACK_WIDTH;
        const left = Math.min(rect.left, window.innerWidth - dropdownWidth - SCREEN_EDGE_PADDING);
        setPos({ top: rect.bottom + DROPDOWN_OFFSET_Y, left: Math.max(SCREEN_EDGE_PADDING, left) });
    }, [anchorRef]);

    return (
        <div ref={dropdownRef} data-filter-dropdown="true" className={styles.FilterDropdown} style={{ top: pos.top, left: pos.left }}>
            <div className={styles.FilterSearchWrapper}>
                <SearchIcon width="14" height="14" stroke="#9ca3af" aria-hidden="true" />
                <input type="text" placeholder="Search" value={search} onChange={(e) => onSearchChange(e.target.value)} autoFocus />
            </div>
            <div className={styles.FilterOptionsList}>
                {options.map((opt) => (
                    <label key={opt.value} className={styles.FilterOption}>
                        <input
                            type="checkbox"
                            checked={selectedValues.includes(opt.value.toLowerCase())}
                            onChange={() => onToggleOption(opt.value)}
                        />
                        <span
                            className={styles.StatusPill}
                            style={opt.backgroundColor ? { backgroundColor: opt.backgroundColor, color: opt.labelColor } : undefined}
                        >
                            {opt.label}
                        </span>
                    </label>
                ))}
                {options.length === 0 && <div className={styles.FilterNoOptions}>No options found</div>}
            </div>
        </div>
    );
};

// ─── Relationship Cell ────────────────────────────────────────────────────────

type RelationshipCellProps = {
    entities: NetworkRelatedPartyEntity[];
};

const RelationshipCell: React.FC<RelationshipCellProps> = ({ entities }) => {
    const [showMore, setShowMore] = useState(false);
    const moreRef = useRef<HTMLSpanElement>(null);
    const [popoverPos, setPopoverPos] = useState({ top: 0, left: 0, below: true });

    if (entities.length === 0) {
        return <div className={styles.RelationshipCol}>-</div>;
    }

    const [first, ...rest] = entities;

    const textRef = useRef<HTMLSpanElement>(null);

    const handleMouseEnter = () => {
        if (!moreRef.current) return;
        const isTruncated = textRef.current ? textRef.current.scrollWidth > textRef.current.clientWidth : false;
        if (rest.length === 0 && !isTruncated) return;
        const rect = moreRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const showBelow = spaceBelow > 200;
        setPopoverPos({
            top: showBelow ? rect.bottom + 4 : rect.top - 4,
            left: rect.left,
            below: showBelow,
        });
        setShowMore(true);
    };

    return (
        <div className={styles.RelationshipCol}>
            <span
                ref={moreRef}
                className={clsx(styles.StatusPill, styles.RelationshipPill, rest.length === 0 && styles.RelationshipPillSingle)}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={() => setShowMore(false)}
            >
                <span
                    ref={textRef}
                    className={clsx(
                        styles.RelationshipText,
                        rest.length > 0 && styles.RelationshipTextWithMore,
                        rest.length === 0 && styles.RelationshipTextSingle,
                    )}
                >
                    {first.displayText}
                </span>
                {rest.length > 0 && <span className={styles.MoreLink}>+{rest.length} more</span>}
            </span>
            {showMore &&
                ReactDOM.createPortal(
                    <div
                        className={styles.RelationshipPopover}
                        style={
                            popoverPos.below
                                ? { top: popoverPos.top, left: popoverPos.left }
                                : { bottom: window.innerHeight - popoverPos.top, left: popoverPos.left }
                        }
                        onMouseEnter={() => setShowMore(true)}
                        onMouseLeave={() => setShowMore(false)}
                    >
                        {entities.map((e, i) => (
                            <div key={i} className={styles.PopoverItem}>
                                <span className={styles.PopoverBullet}>•</span>
                                {e.displayText}
                            </div>
                        ))}
                    </div>,
                    document.body,
                )}
        </div>
    );
};

// ─── Related Party Row ────────────────────────────────────────────────────────

function formatDate(timestamp: number | undefined): string {
    if (!timestamp) return '-';
    const d = new Date(timestamp);
    if (isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

type RelatedPartyRowProps = {
    company: NetworkRelatedPartyCompany;
    statusOptions: StatusOption[];
    relationshipStatusOptions: StatusOption[];
};

const RelatedPartyRow: React.FC<RelatedPartyRowProps> = ({ company, statusOptions, relationshipStatusOptions }) => {
    const [hovered, setHovered] = useState(false);
    const displayName = company.orgName || '-';
    const redirectUrl = company.orgId
        ? `${window.location.origin}/investor/app/portfolio/${company.orgId}?fetchType=all&page=1&type=scan&tab=aicaReport&selectedTab=overview`
        : '';
    const statusMeta = getStatusMeta(company.orgStatus, statusOptions);
    const relStatusMeta = getStatusMeta(company.orgRelationshipStatus, relationshipStatusOptions);
    const entities = company.matches ?? [];

    return (
        <div className={clsx(styles.RPTableRow, styles.RPDataRow)}>
            <div className={styles.NameCol}>
                {company.orgId ? (
                    <Tooltip content={AICA_REPORT_AVAILABLE_TOOLTIP} direction="top" usePortal={true} fitContent={true}>
                        <a
                            className={styles.DrawerCompanyLink}
                            href={redirectUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onMouseEnter={() => setHovered(true)}
                            onMouseLeave={() => setHovered(false)}
                        >
                            <div className={styles.DrawerCompanyNameCell}>
                                <img src={hovered ? purpleRedirectIcon : purpleOfficeIcon} alt="" className={styles.CompanyIcon} />
                                <span className={styles.DrawerCompanyName} title={displayName}>
                                    {displayName}
                                </span>
                            </div>
                        </a>
                    </Tooltip>
                ) : (
                    <div className={styles.DrawerCompanyNameCell}>
                        <img src={purpleOfficeIcon} alt="" className={styles.CompanyIcon} />
                        <span className={styles.DrawerCompanyName} title={displayName}>
                            {displayName}
                        </span>
                    </div>
                )}
            </div>
            <RelationshipCell entities={entities} />
            <div className={styles.StatusCol}>
                {statusMeta ? (
                    <span
                        className={styles.StatusPill}
                        style={{ backgroundColor: statusMeta.backgroundColor, color: statusMeta.labelColor }}
                    >
                        {statusMeta.label}
                    </span>
                ) : (
                    <span className={clsx(styles.StatusPill, styles.StatusPillFallback)}>{company.orgStatus || '-'}</span>
                )}
            </div>
            <div className={styles.RelationshipStatusCol}>
                {relStatusMeta ? (
                    <span
                        className={styles.StatusPill}
                        style={{ backgroundColor: relStatusMeta.backgroundColor, color: relStatusMeta.labelColor }}
                    >
                        {relStatusMeta.label}
                    </span>
                ) : (
                    <span className={clsx(styles.StatusPill, styles.StatusPillFallback)}>{company.orgRelationshipStatus || '-'}</span>
                )}
            </div>
            <div className={styles.UnlockDateCol}>
                <span className={styles.DateText}>{formatDate(company.unlockDate)}</span>
            </div>
        </div>
    );
};

// ─── Related Parties Tab (main export) ───────────────────────────────────────

type RPFilterKey = 'relationship' | 'status' | 'networkRelationship';

export type RelatedPartiesTabProps = {
    data: CompaniesNetworkData;
    statusOptions: StatusOption[];
    relationshipStatusOptions: StatusOption[];
};

const RelatedPartiesTab: React.FC<RelatedPartiesTabProps> = ({ data, statusOptions, relationshipStatusOptions }) => {
    const networkEntities = data.networkRelatedPartyEntities ?? {};

    // Per RECUR-25227: Companies Mentioned (CMs) come from related-party data discovery (UW),
    // not from vendor/customer GST network. The polus response returns each CM with its enriched
    // metadata (orgName, status, unlockDate) and the list of matching relationships.
    const companies = useMemo<NetworkRelatedPartyCompany[]>(
        () => Object.values(networkEntities).filter((cm) => cm && (cm.matches?.length ?? 0) > 0),
        [networkEntities],
    );

    const [activeFilter, setActiveFilter] = useState<RPFilterKey | null>(null);
    const [filterSearch, setFilterSearch] = useState('');
    const [relationshipFilters, setRelationshipFilters] = useState<string[]>([]);
    const [statusFilters, setStatusFilters] = useState<string[]>([]);
    const [networkFilters, setNetworkFilters] = useState<string[]>([]);
    const [sortConfig, setSortConfig] = useState<SortConfig>({ field: '', direction: null });

    const handleSort = (field: string) => {
        setSortConfig((prev) => {
            if (prev.field !== field) return { field, direction: SORT_DIRECTIONS.ASC };
            if (prev.direction === SORT_DIRECTIONS.ASC) return { field, direction: SORT_DIRECTIONS.DESC };
            return { field: '', direction: null };
        });
    };

    const relationshipBtnRef = useRef<HTMLButtonElement>(null);
    const statusBtnRef = useRef<HTMLButtonElement>(null);
    const networkBtnRef = useRef<HTMLButtonElement>(null);
    const tableRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!activeFilter) return;
        const handler = (e: MouseEvent) => {
            const isBtn =
                relationshipBtnRef.current?.contains(e.target as Node) ||
                statusBtnRef.current?.contains(e.target as Node) ||
                networkBtnRef.current?.contains(e.target as Node);
            const isDropdown = !!(e.target as Element)?.closest?.('[data-filter-dropdown]');
            if (!isBtn && !isDropdown) {
                setActiveFilter(null);
                setFilterSearch('');
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [activeFilter]);

    useEffect(() => {
        const el = tableRef.current;
        if (!el) return;
        const handler = () => {
            setActiveFilter(null);
            setFilterSearch('');
        };
        el.addEventListener('scroll', handler);
        return () => el.removeEventListener('scroll', handler);
    }, []);

    const relationshipFilterOptions = useMemo<StatusOption[]>(() => {
        const types = new Set<string>();
        Object.values(networkEntities).forEach((cm) => (cm.matches ?? []).forEach((e) => types.add(e.relationshipType)));
        return Array.from(types).map((rt) => ({ value: rt, label: toRelationshipLabel(rt) }));
    }, [networkEntities]);

    const toggleFilter = (key: RPFilterKey) => {
        setActiveFilter((prev) => (prev === key ? null : key));
        setFilterSearch('');
    };

    const toggleValue = (setter: React.Dispatch<React.SetStateAction<string[]>>, value: string) => {
        const lower = value.toLowerCase();
        setter((prev) => (prev.includes(lower) ? prev.filter((v) => v !== lower) : [...prev, lower]));
    };

    const filtered = useMemo(() => {
        return companies.filter((c) => {
            if (statusFilters.length > 0 && !statusFilters.includes(c.orgStatus?.toLowerCase() ?? '')) return false;
            if (networkFilters.length > 0 && !networkFilters.includes(c.orgRelationshipStatus?.toLowerCase() ?? '')) return false;
            if (relationshipFilters.length > 0) {
                const hasMatch = (c.matches ?? []).some((e) => relationshipFilters.includes(e.relationshipType.toLowerCase()));
                if (!hasMatch) return false;
            }
            return true;
        });
    }, [companies, statusFilters, networkFilters, relationshipFilters]);

    const sorted = useMemo(() => {
        if (!sortConfig.field || !sortConfig.direction) return filtered;
        return [...filtered].sort((a, b) => {
            const dateA = a.unlockDate ?? 0;
            const dateB = b.unlockDate ?? 0;
            return sortConfig.direction === SORT_DIRECTIONS.ASC ? dateA - dateB : dateB - dateA;
        });
    }, [filtered, sortConfig]);

    const activeOptions =
        activeFilter === 'relationship' ? relationshipFilterOptions : activeFilter === 'status' ? statusOptions : relationshipStatusOptions;

    const activeSelectedValues =
        activeFilter === 'relationship' ? relationshipFilters : activeFilter === 'status' ? statusFilters : networkFilters;

    const visibleOptions = filterSearch
        ? activeOptions.filter((o) => o.label.toLowerCase().includes(filterSearch.toLowerCase()))
        : activeOptions;

    const activeBtnRef = activeFilter === 'relationship' ? relationshipBtnRef : activeFilter === 'status' ? statusBtnRef : networkBtnRef;

    if (companies.length === 0) {
        return (
            <div className={styles.EmptyState}>
                <img src={noDataFoundEmptyState} alt="No data" style={{ width: '120px', height: 'auto', objectFit: 'contain' }} />
                <span className={styles.EmptyStateText}>No Data Available</span>
            </div>
        );
    }

    return (
        <>
            <div className={styles.DrawerTable} ref={tableRef}>
                <div className={clsx(styles.RPTableRow, styles.DrawerTableHead)}>
                    <div className={styles.NameCol}>Related Entity</div>
                    <div className={styles.RelationshipCol}>
                        <span>Relationship</span>
                        <button
                            ref={relationshipBtnRef}
                            type="button"
                            className={styles.FilterBtn}
                            onClick={() => toggleFilter('relationship')}
                            aria-label="Filter by Relationship"
                        >
                            <img
                                src={
                                    relationshipFilters.length > 0 || activeFilter === 'relationship'
                                        ? filterIconMUPurpleDark
                                        : filterIconMUDark
                                }
                                alt=""
                            />
                        </button>
                    </div>
                    <div className={styles.StatusCol}>
                        <span>Status</span>
                        <button
                            ref={statusBtnRef}
                            type="button"
                            className={styles.FilterBtn}
                            onClick={() => toggleFilter('status')}
                            aria-label="Filter by Status"
                        >
                            <img
                                src={statusFilters.length > 0 || activeFilter === 'status' ? filterIconMUPurpleDark : filterIconMUDark}
                                alt=""
                            />
                        </button>
                    </div>
                    <div className={styles.RelationshipStatusCol}>
                        <span>Relationship Status</span>
                        <button
                            ref={networkBtnRef}
                            type="button"
                            className={styles.FilterBtn}
                            onClick={() => toggleFilter('networkRelationship')}
                            aria-label="Filter by Network Relationship"
                        >
                            <img
                                src={
                                    networkFilters.length > 0 || activeFilter === 'networkRelationship'
                                        ? filterIconMUPurpleDark
                                        : filterIconMUDark
                                }
                                alt=""
                            />
                        </button>
                    </div>
                    <div className={styles.UnlockDateCol}>
                        <span>Unlock Date</span>
                        <SortIcon field="unlockDate" sortConfig={sortConfig} onSort={handleSort} alwaysVisible />
                    </div>
                </div>

                {sorted.length === 0 ? (
                    <div className={styles.EmptyState}>
                        <span className={styles.EmptyStateText}>No Data Available</span>
                    </div>
                ) : (
                    sorted.map((company, index) => (
                        <RelatedPartyRow
                            key={`rp-${company.orgId ?? index}`}
                            company={company}
                            statusOptions={statusOptions}
                            relationshipStatusOptions={relationshipStatusOptions}
                        />
                    ))
                )}
            </div>

            {activeFilter &&
                ReactDOM.createPortal(
                    <FilterDropdown
                        anchorRef={activeBtnRef}
                        options={visibleOptions}
                        selectedValues={activeSelectedValues}
                        search={filterSearch}
                        onSearchChange={setFilterSearch}
                        onToggleOption={(val) => {
                            if (activeFilter === 'relationship') toggleValue(setRelationshipFilters, val);
                            else if (activeFilter === 'status') toggleValue(setStatusFilters, val);
                            else toggleValue(setNetworkFilters, val);
                        }}
                    />,
                    document.body,
                )}
        </>
    );
};

export default RelatedPartiesTab;
